/* eslint-disable react/forbid-prop-types */
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, Stack, Typography, Chip, alpha, Tooltip,
    CircularProgress, IconButton,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import useCopyToClipboard from 'src/hooks/use-copy-to-clipboard';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import { common } from 'src/theme/palette';

/* ──────────────────────────────────────────────────────────────────────────
   AdvancedDispatchBar
   ────────────────────────────────────────
   Post-process channel dispatch surface for the Advanced view.

   Mirrors the architecture Quick Send adopted: a single pre-flight POST
   creates the ConsentRequest(s) with channel=COPY_LINK; subsequent
   per-channel sends reuse the same access_token via
   ``consent_request_ids``. The user sees one URL, can copy it
   immediately, and dispatches Email / WhatsApp as separate actions.

   Rendered conditionally above the dialog footer in Advanced mode.
   ────────────────────────────────────────────────────────────────────────── */

const STATUS_COLORS = {
    IDLE: '#637381',
    SENDING: '#FFAB00',
    SENT: '#00AB55',
    FAILED: '#B72136',
};

const ChannelPill = ({ channel, status, label, icon, disabled, disabledReason, onClick }) => {
    const color = STATUS_COLORS[status] || STATUS_COLORS.IDLE;
    const isSent = status === 'SENT';
    const isSending = status === 'SENDING';
    return (
        <Tooltip title={disabled ? disabledReason : ''} disableInteractive>
            <span>
                <Button
                    size="small"
                    variant={isSent ? 'outlined' : 'contained'}
                    disabled={disabled || isSending}
                    onClick={onClick}
                    startIcon={isSending
                        ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                        : <Iconify icon={isSent ? 'eva:checkmark-circle-2-fill' : icon} width={14} />}
                    aria-label={`Send via ${channel}`}
                    sx={{
                        borderRadius: '999px',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '12px',
                        px: 1.75,
                        py: 0.4,
                        minHeight: 28,
                        ...(isSent ? {
                            color,
                            borderColor: alpha(color, 0.4),
                            bgcolor: alpha(color, 0.06),
                            '&:hover': { bgcolor: alpha(color, 0.1), borderColor: color },
                        } : {
                            bgcolor: color === STATUS_COLORS.IDLE ? common.mintyZest : color,
                            color: '#fff',
                            '&:hover': {
                                bgcolor: color === STATUS_COLORS.IDLE ? '#007B40' : color,
                            },
                        }),
                        '&.Mui-disabled': {
                            bgcolor: alpha('#919EAB', 0.16),
                            color: '#919EAB',
                            boxShadow: 'none',
                        },
                    }}
                >
                    {(() => {
                        if (isSending) return `Sending ${label}…`;
                        if (isSent) return `${label} sent`;
                        return `Send via ${label}`;
                    })()}
                </Button>
            </span>
        </Tooltip>
    );
};
ChannelPill.propTypes = {
    channel: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['IDLE', 'SENDING', 'SENT', 'FAILED']).isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    disabledReason: PropTypes.string,
    onClick: PropTypes.func.isRequired,
};
ChannelPill.defaultProps = { disabled: false, disabledReason: '' };

const AdvancedDispatchBar = ({
    sendResult,
    channels,
    audienceMode,
    selectedLead,
    patientEmail,
    patientPhone,
    emailTemplateId,
    whatsappTemplateId,
    channelStatus,
    onSendChannel,
    onReset,
}) => {
    const { copy } = useCopyToClipboard();
    const { enqueueSnackbar } = useCustomSnackbar();
    const [copyHover, setCopyHover] = useState(false);

    const firstLink = sendResult?.links?.[0];
    const formUrl = firstLink?.url || sendResult?.formUrl || '';
    const linksCount = (sendResult?.links || []).length;

    // Disable rules per channel — mirror canSendEmail / canSendWhatsApp
    // in Quick Send Step 4 so the UI doesn't let the user trigger an
    // obviously-broken POST.
    const isOnePatient = audienceMode === 'ONE_PATIENT';
    const emailDisabled = useMemo(() => {
        if (isOnePatient && !patientEmail) return 'Patient has no email on file';
        if (!emailTemplateId) return 'Pick an email template';
        return '';
    }, [isOnePatient, patientEmail, emailTemplateId]);
    const whatsappDisabled = useMemo(() => {
        if (isOnePatient && !patientPhone) return 'Patient has no phone on file';
        if (!whatsappTemplateId) return 'Pick a WhatsApp template';
        return '';
    }, [isOnePatient, patientPhone, whatsappTemplateId]);

    const handleCopy = async () => {
        if (!formUrl) return;
        const ok = await copy(formUrl);
        enqueueSnackbar(ok ? 'Link copied' : 'Failed to copy', {
            variant: ok ? 'success' : 'error',
        });
    };

    return (
        <Box
            sx={{
                px: 2.5, py: 1.25,
                borderTop: '1px solid', borderColor: 'divider',
                bgcolor: alpha(common.mintyZest, 0.04),
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {/* Headline + reset */}
            <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="eva:checkmark-circle-2-fill" width={16} sx={{ color: common.mintyZest, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#1C252E', flex: 1, minWidth: 0 }}>
                    {linksCount > 1
                        ? `${linksCount} links ready`
                        : 'Link ready'}
                </Typography>
                {sendResult?.expiresAt && (
                    <Chip
                        size="small"
                        label={`Expires ${new Date(sendResult.expiresAt).toLocaleDateString()}`}
                        sx={{
                            height: 18, fontSize: '10px', fontWeight: 700,
                            bgcolor: alpha(common.mintyZest, 0.1),
                            color: common.mintyZest,
                        }}
                    />
                )}
                <Tooltip title="Start over with a new link" disableInteractive>
                    <IconButton
                        size="small"
                        onClick={onReset}
                        aria-label="Start over"
                        sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                    >
                        <Iconify icon="eva:refresh-fill" width={14} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* Link banner */}
            {formUrl && (
                <Box
                    onMouseEnter={() => setCopyHover(true)}
                    onMouseLeave={() => setCopyHover(false)}
                    onClick={handleCopy}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCopy(); }}
                    sx={{
                        cursor: 'pointer',
                        px: 1, py: 0.6, borderRadius: '8px',
                        bgcolor: '#fff',
                        border: '1px solid', borderColor: alpha(common.mintyZest, 0.2),
                        display: 'flex', alignItems: 'center', gap: 0.75,
                        '&:hover': { borderColor: alpha(common.mintyZest, 0.4) },
                    }}
                >
                    <Iconify icon="eva:link-2-outline" width={14} sx={{ color: common.mintyZest, flexShrink: 0 }} />
                    <Typography
                        sx={{
                            flex: 1, fontFamily: 'ui-monospace,Menlo,monospace',
                            fontSize: '11px', color: common.mintyZest,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                    >
                        {formUrl}
                    </Typography>
                    <Iconify
                        icon={copyHover ? 'eva:copy-fill' : 'eva:copy-outline'}
                        width={14}
                        sx={{ color: common.mintyZest, flexShrink: 0 }}
                    />
                </Box>
            )}

            {/* Channel dispatch row — only channels the user picked are rendered.
                COPY/QR were satisfied by the pre-flight; EMAIL/WHATSAPP are
                separate dispatches that reuse the CR via consent_request_ids. */}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                {channels.includes('EMAIL') && (
                    <ChannelPill
                        channel="EMAIL"
                        status={channelStatus.EMAIL || 'IDLE'}
                        label="Email"
                        icon="eva:email-outline"
                        disabled={Boolean(emailDisabled)}
                        disabledReason={emailDisabled}
                        onClick={() => onSendChannel('EMAIL')}
                    />
                )}
                {channels.includes('WHATSAPP') && (
                    <ChannelPill
                        channel="WHATSAPP"
                        status={channelStatus.WHATSAPP || 'IDLE'}
                        label="WhatsApp"
                        icon="ic:baseline-whatsapp"
                        disabled={Boolean(whatsappDisabled)}
                        disabledReason={whatsappDisabled}
                        onClick={() => onSendChannel('WHATSAPP')}
                    />
                )}
                {(channels.includes('COPY') || channels.includes('COPY_LINK')) && (
                    <Chip
                        size="small"
                        icon={<Iconify icon="eva:link-2-outline" width={12} />}
                        label="Copy link · ready"
                        sx={{
                            height: 24, fontSize: '11px', fontWeight: 700,
                            color: STATUS_COLORS.SENT,
                            bgcolor: alpha(STATUS_COLORS.SENT, 0.08),
                            '& .MuiChip-icon': { color: STATUS_COLORS.SENT, ml: 0.5 },
                        }}
                    />
                )}
                {channels.includes('QR') && (
                    <Chip
                        size="small"
                        icon={<Iconify icon="eva:grid-outline" width={12} />}
                        label="QR · scan to open"
                        sx={{
                            height: 24, fontSize: '11px', fontWeight: 700,
                            color: STATUS_COLORS.SENT,
                            bgcolor: alpha(STATUS_COLORS.SENT, 0.08),
                            '& .MuiChip-icon': { color: STATUS_COLORS.SENT, ml: 0.5 },
                        }}
                    />
                )}
                {selectedLead && isOnePatient && (
                    <Typography
                        sx={{
                            fontSize: '10.5px', color: 'text.secondary',
                            ml: 'auto',
                        }}
                    >
                        {`To ${selectedLead.first_name || ''} ${selectedLead.last_name || ''}`.trim()}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

AdvancedDispatchBar.propTypes = {
    sendResult: PropTypes.shape({
        links: PropTypes.array,
        formUrl: PropTypes.string,
        expiresAt: PropTypes.string,
    }).isRequired,
    channels: PropTypes.arrayOf(PropTypes.string).isRequired,
    audienceMode: PropTypes.oneOf(['ONE_PATIENT', 'PATIENT_LIST', 'ANONYMOUS']).isRequired,
    selectedLead: PropTypes.object,
    patientEmail: PropTypes.string,
    patientPhone: PropTypes.string,
    emailTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    whatsappTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    channelStatus: PropTypes.object.isRequired,
    onSendChannel: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
};

AdvancedDispatchBar.defaultProps = {
    selectedLead: null,
    patientEmail: '',
    patientPhone: '',
    emailTemplateId: null,
    whatsappTemplateId: null,
};

export default AdvancedDispatchBar;
